import {Component,createEffect,createSignal,Accessor, Setter,For,onCleanup,onMount,Show} from 'solid-js';
import {toQueryString, getPageRange,getPageBlockStart, getOrSetAsync,cleanImagePath} from '../API';
import { CMS } from '../cms';
import { makeAccessor, SelectList, RadioButton, AlphabeticalList, PageList, Checkbox } from '../Controls';
import { createStore } from 'solid-js/store';

// types
type YesNo = "" | "Yes" | "No";
type OrderBy = string | "ByName" | "ByDateAdded" | "ByTotalArtworks" | "ByRandom" | "ByAge" | "ByNationality" | "ByBirth" | "ByDeath";
type QueryState = "NotStarted" | "Loading" | "Success" | "Error";
type Artist = {
    id: number;
    memberLikes: Accessor<boolean>;
    setMemberLikes: Setter<boolean>;
    backgroundColor: string;
    artistUrl: string;
    firstName: string;
    lastName: string;
    surnameFirst: string;
    primaryImageUrl: string;
    imageHeight: number;
    imageWidth: number;
    dateAdded: string;
    dateRange: string;
    age: number | null;
    details: string;
    artworkTotal: number;
    artworks: string;
};

type SimilarArtistsTabProps = {
    artistId: number,
    isLoggedIn: boolean
}

type Filter = {
    SimilarArtistsToId: number,
    SameMovements: boolean,
    SameProfessions: boolean,
    BornAroundSameTime: boolean,
    Page: number,
    PageRows: number,
    Order: OrderBy,
    Direction: string
}

export const SimilarArtistsTab : Component<SimilarArtistsTabProps> = (props) => {
    const [queryState, setQueryState] = createSignal<QueryState>("NotStarted");
    const [artists, setArtists] = createSignal<Artist[]>([]);
    const [startsWith, setStartsWith] = createSignal<string>("");
    const [filter, setFilter] = createStore<Filter>({
        SimilarArtistsToId: props.artistId,
        SameMovements: true,
        SameProfessions: true,
        BornAroundSameTime: true,
        Page: 1,
        PageRows: 20,
        Order: "ByName",
        Direction: "Ascending"
    } as Filter);
    const [pages, setPages] = createSignal<number[]>([1]);
    const [letters, setLetters] = createSignal<string[]>([]);
    const [maxPage, setMaxPage] = createSignal<number>(1);
    const list = {
        yesNos: makeAccessor([
            { Text: "", Value: "" },
            { Text: "Yes", Value: "Yes" },
            { Text: "No", Value: "No" }
        ]),
        orderBy: makeAccessor([
            { Text: "By name", Value: "ByName" },
            { Text: "By nationality", Value: "ByNationality" },
            { Text: "By birth", Value: "ByBirth" },
            { Text: "By death", Value: "ByDeath" },
            { Text: "By age", Value: "ByAge" },
            { Text: "By date added", Value: "ByDateAdded" },
            { Text: "By total artworks", Value: "ByTotalArtworks"},
            { Text: "By random artist", Value: "ByRandom"},
        ])
    };
    const resetFields = async () => {
        setFilter("Page", 1);
        setMaxPage(1);
        setFilter("Order", "ByName");
        setFilter("Direction", "Ascending");
        await search();
    };
    const pageBlockSize = 20;
    const skipToPreviousBlock = async () =>{
        const pageBlockStart = getPageBlockStart(filter.Page, pageBlockSize);
        if (pageBlockStart - pageBlockSize >= 1) {
            const previousPageBlockStart = pageBlockStart - pageBlockSize;
            setFilter("Page", previousPageBlockStart);
            await search();
        }
    };
    const skipToNextBlock = async () =>{
        const pageBlockStart = getPageBlockStart(filter.Page, pageBlockSize);
        if (pageBlockStart + pageBlockSize <= maxPage()) {
            const nextPageBlockStart = pageBlockStart + pageBlockSize;
            setFilter("Page", nextPageBlockStart);
            await search();
        }
    };
    const searchByStartLetter = async (letter: string) => {
        setStartsWith(letter == startsWith() ? "" : letter);
        await search();
    };
    const toggleMemberLikes = async (artist: Artist) => {
        const likes = artist.memberLikes();
        artist.setMemberLikes(!likes);
        const data = { artistId: artist.id, likes: !likes };
        const qs = toQueryString(data);
        const response = await fetch("/Museum/LikesArtist?" + qs);
        const json = await response.json();
        if (!json.Success) {
            artist.setMemberLikes(likes);
        }
    };    
    const searchByPage = async (page: number) => {
        setFilter("Page", page);
        await search();
    }
    const submit = async (e: Event) => {
        e.preventDefault();
        await search();
    };
    const retrySearch = async () => {
        await search();
    };
    const getFilter = () => {
        const parameters = {
            SimilarArtistsToId: props.artistId,
            SameMovements: filter.SameMovements,
            SameProfessions: filter.SameProfessions,
            BornAroundSameTime: filter.BornAroundSameTime,
            Page: filter.Page,
            PageRows: filter.PageRows,
            Order: filter.Order,
            Direction: filter.Direction
        };
        return parameters;
    };
    const getData = async () => {
        const filter = toQueryString(getFilter());
        const json = await getOrSetAsync("similar-artists/" + filter, async () => {
            const response = await fetch("/Museum/GetSearchArtistsJson?" + filter);
            return await response.json();
        });
        return json;
    };
    const search = async (reset = true) => {
        setQueryState("Loading");
        const json = await getData();
        setQueryState("Success");
        const results: Artist[] = json.Data.Results.map((item: any) => {
            const [memberLikes, setMemberLikes] = createSignal<boolean>(item.MemberLikes as boolean);
            const result: Artist = {
                id: item.Id,
                memberLikes: memberLikes,
                setMemberLikes: setMemberLikes,
                backgroundColor: item.BackgroundColor,
                firstName: item.FirstName,
                lastName: item.LastName,
                surnameFirst: item.SurnameFirst,            
                primaryImageUrl: item.PrimaryImageUrl,
                imageHeight: item.ImageHeight || 0,
                imageWidth: item.ImageWidth || 0,
                artworkTotal: item.ArtworkTotal,
                details: toDetails(item),
                age: item.Age,
                artistUrl: `/artists/${item.Slug}/${item.Id}`,
                dateAdded: item.DateAdded,
                dateRange: item.DateRange,
                artworks: item.ArtworkTotal > 1 ? "artworks" : "artwork"
            };
            return result;
        });
        if (props.isLoggedIn){
            
            await updateMemberLikes(results);
        }
        const _results = reset ? results : artists().concat(results);
        setArtists(_results);
        setMaxPage(json.Data.MaxPage);
        const pageNumbers = getPageRange(filter.Page, pageBlockSize, maxPage());
        setPages(pageNumbers);        
        return json;
    };
    const updateMemberLikes = async (results: Artist[]) => {
        if (!results?.length) return;
        const ids = results.map(x => x.id).join(",");
        const qs = toQueryString({ ids: ids });
        const response = await fetch("/Museum/SolidFavouriteArtistsJson?" + qs);
        const json = await response.json();
        if (!json.Success) {
            return;
        }
        const favourites = json.Data.Results as number[];
        const matches = results.filter(x => favourites.indexOf(x.id) >= 0);
        matches.forEach(x => x.setMemberLikes(true));
    };
    const toDetails = (artist: any) => {
        var parts = [];
        if (!!artist.Nationality) parts.push(artist.Nationality);
        if (!!artist.Movements) parts.push(artist.Movements);
        if (!!artist.Professions && artist.Professions.length > 0) {
            parts.push(artist.Professions);
        } else {
            parts.push("artist");
        }
        const _details = parts.join(" ");
        const details = capitaliseFirstLetter(_details.trim());
        return details;
    }
    const capitaliseFirstLetter = (value: string) => {
        return value.charAt(0).toUpperCase() + value.slice(1);
    };
    const loadNextPage = async () => {
        if (filter.Page >= maxPage()) {
            return;
        }
        setFilter("Page", filter.Page + 1);
        await search(false);
    };
    const createAlphabet = () => {
        const alpha = Array.from(Array(26)).map((e, i) => i + 65);
        const alphabet = alpha.map((x) => String.fromCharCode(x));
        return alphabet;
    };
    const offset = () => 1000;
    const handleScroll = async () => {
        const scrollBar = window.innerHeight + window.scrollY;
        const viewPort = document.body.offsetHeight - offset();
        console.log({ scrollBar, viewPort });
        if (scrollBar >= viewPort) {
            if (queryState() != "Loading") {
                await loadNextPage();
            }
        }
      };
    const checkMobile = () => screen.width <= 768;
    const [isMobile, setIsMobile] = createSignal<boolean>(checkMobile());
    const width = () => isMobile() ? screen.width : 350;
    const updateIsMobile = () => setIsMobile(checkMobile());
    createEffect(() => {
        window.addEventListener('resize', updateIsMobile);
    });
    onCleanup(async () => {
        window.removeEventListener('resize', updateIsMobile);
    });
    createEffect(async () => {
        window.addEventListener('scroll', handleScroll);
    });
    onCleanup(async () => {
        window.removeEventListener('scroll', handleScroll);
    });
    onMount(async () => {
        setLetters(createAlphabet());
        await search();
    });
return (
<div>   
    <section class="search m-b-xxl">
        <div class="row">
            <div class="col-md-12">
                <form onSubmit={submit}>
                    {/* <div class="row">
                        <div class="col-md-2 col-xs-6">
                            <label>
                                <CMS id="label::sort-by">Same movements?</CMS>
                            </label>
                            <Checkbox name="SameMovements" value={filter.SameMovements} field={[() => filter.SameMovements, (value) => setFilter("SameMovements", value)]} />
                        </div>
                        <div class="col-md-2 col-xs-6">
                            <label>
                                <CMS id="label::sort-by">Same professions?</CMS>
                            </label>
                            <Checkbox name="SameProfessions" value={filter.SameProfessions} field={[() => filter.SameProfessions, (value) => setFilter("SameProfessions", value)]} />
                        </div>
                        <div class="col-md-2 col-xs-6">
                            <label>
                                <CMS id="label::sort-by">Born around the same time?</CMS>
                            </label>
                            <Checkbox name="BirthDateRange" value={filter.BornAroundSameTime} field={[() => filter.BornAroundSameTime, (value) => setFilter("BirthDateRange", value)]} />
                        </div>
                    </div> */}
                    <div style="font-size: 1.25rem; padding: 10px;">
                        This is a list of artists similar to the selected artist based on shared movements, professions, and birth date range.
                    </div>
                    <div class="row">
                        <div class="col-md-2 col-xs-6">
                            <label>
                                <CMS id="label::sort-by">Sort by</CMS>
                            </label>
                            <SelectList field={[() => filter.Order, (value) => setFilter("Order", value)]} list={list.orderBy}></SelectList>
                        </div>
                        <div class="col-md-2 col-xs-6">
                            <label>
                                <CMS id="label::sort-direction">Sort direction</CMS>
                            </label>
                            <div>
                                <RadioButton name="SortDirection" field={[() => filter.Direction, (value) => setFilter("Direction", value)]} value="Ascending"></RadioButton>
                                <RadioButton name="SortDirection" field={[() => filter.Direction, (value) => setFilter("Direction", value)]} value="Descending"></RadioButton>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-2" style="margin-top: 15px;">
                            <button type="button" class="btn btn-default btn-white" style="margin-right: 10px;" onClick={(e) => { e.preventDefault(); resetFields(); }}>
                                <img src="/Content/icons/clear.svg" style="width: 25px; margin-right: 5px;" />
                                <div class="bold-button-text"><CMS id="button::clear">Clear</CMS></div>
                            </button>
                            <button type="submit" class="btn btn-primary">
                                <img src="/Content/icons/magnifiying-glass.svg" style="width: 25px; filter: invert(100%); margin-right: 5px;" />
                                <div class="bold-button-text"><CMS id="button::search">Search</CMS></div>
                            </button>
                        </div>
                    </div>
                    <hr />
                    <div>
                        <div style="display: inline-block; padding-left: 20px; vertical-align: top;">
                            <label>
                                <CMS id="label::artists-starting-with">Artists starting with</CMS>
                            </label>
                            <div>
                                <AlphabeticalList letter={startsWith} onClick={searchByStartLetter}></AlphabeticalList>                                
                            </div>
                            <label style="margin-top: 10px;">
                                <CMS id="label::page">Page</CMS>
                            </label>
                            <div>
                                <span class="alphabet-index" onClick={skipToPreviousBlock}><span class="fa fa-chevron-left"></span></span>
                                <PageList page={() => filter.Page} pages={pages} onClick={searchByPage}></PageList>
                                <span class="alphabet-index" onClick={skipToNextBlock}><span class="fa fa-chevron-right"></span></span>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </section>
    <section class="salon-results" style="margin-bottom: -40px; margin-top: 20px;">
        <div style="background-color: #333; position: relative;">
            <div style="height: 40px;">
                               
            </div>
            <div class="finalist-grid">
                <Show when={queryState() == "Success" && artists().length === 0}>
                    <div style="text-align: center;">
                        <h2 style="font-size: 3em; color: #fff; padding: 40px; font-family: 'Oswald';">
                            <CMS id="message::search-returned-no-results">This search returned no results.</CMS>
                        </h2>
                    </div>
                </Show>
                <Show when={queryState() == "Error"}>
                    <div style="text-align: center;">
                        <div style="padding: 40px;">
                            <h2 style="font-size: 3em; color: #fff; font-family: 'Oswald';">
                                <CMS id="message::unexpected-search-error">There was an unexpected error. Please try again.</CMS>
                            </h2>
                            <div style="font-size: 2em; margin-top: 30px;">
                                <button type="button" onClick={retrySearch}><span class="fa fa-refresh"></span> <CMS id="button::retry">Retry</CMS></button>
                            </div>
                        </div>
                    </div>
                </Show>
                <div style="text-align: center;">
                    <Show when={artists().length}>
                        <For each={artists()}>{(artist: Artist) =>
                            <div class="grid-item" style="display: inline-block; text-align: left; vertical-align: top;">
                                <div style={{ position: "relative", "background-color": artist.backgroundColor }}>
                                    <a href={artist.artistUrl}>
                                        <Show when={artist.imageHeight > 0}>
                                            <img src={ cleanImagePath(artist.primaryImageUrl) + "?format=jpg&mode=crop&w=" + width()} style={{ width: width() + "px", height: (artist.imageHeight * (width() / artist.imageWidth)) + "px"}} />
                                        </Show>
                                        <Show when={artist.imageHeight === 0}>
                                            <img src={ cleanImagePath(artist.primaryImageUrl) + "?format=jpg&mode=crop&w=" + width()} style={{width: width() + "px"}} />
                                        </Show>
                                    </a>
                                </div>
                                <div class="visit-gallery">
                                    <a href={artist.artistUrl}>
                                        <CMS id="button::visit-gallery">Visit gallery</CMS>
                                    </a>
                                </div>
                                <div class="didactic" style="background-color: #efefef;">
                                    <Show when={props.isLoggedIn}>
                                        <div style="float: right; font-size: 1.5rem; color: goldenrod;">
                                            <span style={{ color: artist.memberLikes() ? "goldenrod" : "#333" }} classList={{ ["fa fa-star"]: artist.memberLikes(), ["fa fa-star-o"]: !artist.memberLikes() }} onClick={() => toggleMemberLikes(artist)}></span>
                                        </div>
                                    </Show>
                                    <h1 style="font-family: 'Oswald'; font-size: 1.5em;">
                                        <a style="text-decoration: underline;" href={artist.artistUrl}>{artist.surnameFirst}</a>
                                    </h1>
                                    <h4>
                                        <span style="margin-right: 5px;">{artist.dateRange}</span>
                                        <Show when={artist.age}>
                                            (aged {artist.age})
                                        </Show>
                                    </h4>
                                    <p style="margin-top: -10px;">
                                        {artist.details}
                                    </p>
                                    <section class="artist-result-stats">
                                        <span class="label label-default">{artist.artworkTotal}</span> <span class="description">{artist.artworks}</span>
                                    </section>
                                    <Show when={!!artist.dateAdded}>
                                        <hr />
                                        <p style="margin:0;">
                                            <span>{artist.dateAdded}</span>
                                        </p>
                                    </Show>                                
                                </div>
                            </div>
                        }</For>
                    </Show>
                </div>            
            </div>
        </div>
    </section>
</div>);
}